///
/// EarthBrowser
///
/// Created by Matt Giger on 1/27/2025
/// Copyright (c) 2025 Geodata Labs
///

import { animation, easing } from "@stovokor/math/animation"
import { matrix } from "@stovokor/math/matrix"
import { quaternion } from "@stovokor/math/quaternion"
import { cross, dot, normal, vec3, vec3lerp } from "@stovokor/math/vector"
import { lerp } from "@stovokor/math/math"

/**
 * Represents a camera's position and orientation in 3D space.
 * This class combines position and orientation data for camera transformations
 * and includes animation capabilities.
 */
export class CameraState {
	public position: vec3
	public forward: vec3
	public up: vec3

	// Animation properties
	private anim = new animation().from(0).to(1).easing(easing.quadraticInOut)
	private updateCallback: () => void = () => {}
	private completionCallback: () => void = () => {}

	constructor(position: vec3 = vec3.zero, forward: vec3 = vec3.negZ, up: vec3 = vec3.unitY) {
		this.position = position
		this.forward = forward
		this.up = up
	}

	/**
	 * Returns the right vector of the camera
	 * @returns The right vector
	 */
	public get right(): vec3 {
		return normal(cross(this.forward, this.up))
	}

	/**
	 * Returns true if an animation is currently running
	 */
	public get animating(): boolean {
		return this.anim.running
	}

	/**
	 * Stops the current animation
	 */
	public stopAnimating(): void {
		this.anim.stop()
		this.completionCallback()
	}

	public onUpdate(callback: () => void): CameraState {
		this.updateCallback = callback
		return this
	}

	public onComplete(callback: () => void): CameraState {
		this.completionCallback = callback
		return this
	}

	/**
	 * Animates the camera transform to a new position and orientation
	 * @param transform - The target camera state or a function that returns the target state
	 * @param duration - The duration of the animation in seconds
	 */
	public animateTo(transform: (() => CameraState), duration: number): CameraState {
		const start = this.position
		const startForward = this.forward
		const startUp = this.up

		this.anim
			.duration(duration)
			.onUpdate((value: number) => {
				// Get the current target (may change if tracking a moving object)
				const currentTarget = transform()

				// Recalculate rotations if target is dynamic
				const fwdRot = quaternion.fromVec3ToVec3(startForward, currentTarget.forward)
				const upRot = quaternion.fromVec3ToVec3(startUp, currentTarget.up)

				// Animate forward using quaternion rotation
				const fquat = fwdRot.pow(value)
				this.forward = fquat.rotateVector(startForward)

				// Animate up using quaternion rotation
				const uquat = upRot.pow(value)
				this.up = uquat.rotateVector(startUp)

				// Interpolate position, tracking the target if it's moving
				const radius = lerp(start.length, currentTarget.position.length, value)
				const pos = vec3lerp(start, currentTarget.position, value)
				this.position = normal(pos).multiply(radius)

				this.updateCallback()
			})
			.onComplete(() => {
				const finalTarget = transform()
				this.forward = finalTarget.forward
				this.up = finalTarget.up
				this.position = finalTarget.position
				this.completionCallback()
			})
			.start()

		return this
	}

	/**
	 * Gets the look toward matrix.
	 * @returns The look toward matrix.
	 */
	public get lookToward(): matrix {
		const pos = this.position
		const fwd = this.forward
		const side = cross(fwd, this.up)
		const nup = cross(side, fwd)
		return new matrix([
			side.x, nup.x, -fwd.x, 0,
			side.y, nup.y, -fwd.y, 0,
			side.z, nup.z, -fwd.z, 0,
			-dot(pos, side), -dot(pos, nup), dot(pos, fwd), 1
		])
	}
}
